import React, { useRef, useEffect, useState } from "react";

const GainCanvas = () => {
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const gainKnobRef = useRef([]);
    const toneKnobRef = useRef([]);
    const levelKnobRef = useRef([]);

    const [gainKnobIndex, setGainKnobIndex] = useState(0);
    const [toneKnobIndex, setToneKnobIndex] = useState(0);
    const [levelKnobIndex, setLevelKnobIndex] = useState(0);

    const [isDragging, setIsDragging] = useState(false);
    const activeKnob = useRef(null);
    const initialClickY = useRef(null);
    const initialKnobIndex = useRef(null);

    const width = 300;
    const height = 500;
    const KNOB_PIXEL_RANGE = 150; // Drag sensitivity range

    const KNOBS = {
        gain: { x: width * 0.14, y: height * 0.09 },
        tone: { x: width * 0.39, y: height * 0.14 },
        level: { x: width * 0.65, y: height * 0.09 },
        size: width * 0.2, // Size of each knob
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        canvas.width = width;
        canvas.height = height;

        const background = new Image();
        background.src = "/MK1OverdriveImages/MK1OverdriveOnRed.png";

        for (let i = 0; i < 300; i++) {
            gainKnobRef.current[i] = new Image();
            gainKnobRef.current[i].src = `/MK1OverdriveImages/MK1Overdrive_${i}.png`;

            toneKnobRef.current[i] = new Image();
            toneKnobRef.current[i].src = `/MK1OverdriveImages/MK1Overdrive_${i}.png`;

            levelKnobRef.current[i] = new Image();
            levelKnobRef.current[i].src = `/MK1OverdriveImages/MK1Overdrive_${i}.png`;
        }

        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            if (imageRef.current) {
                ctx.drawImage(imageRef.current, width * 0.05, height * 0.05, width * 0.9, height * 0.9);
            }

            ctx.drawImage(gainKnobRef.current[gainKnobIndex], KNOBS.gain.x, KNOBS.gain.y, KNOBS.size, KNOBS.size);
            ctx.drawImage(toneKnobRef.current[toneKnobIndex], KNOBS.tone.x, KNOBS.tone.y, KNOBS.size, KNOBS.size);
            ctx.drawImage(levelKnobRef.current[levelKnobIndex], KNOBS.level.x, KNOBS.level.y, KNOBS.size, KNOBS.size);
        };

        draw();

        background.onload = () => {
            imageRef.current = background;
            draw();
        };
    }, [gainKnobIndex, toneKnobIndex, levelKnobIndex]);

    useEffect(() => {
        const canvas = canvasRef.current;

        const handleMouseDown = (event) => handleStart(event);
        const handleMouseMove = (event) => handleMove(event);
        const handleMouseUp = () => setIsDragging(false);

        const handleTouchStart = (event) => handleStart(event.touches[0]);
        const handleTouchMove = (event) => {
            event.preventDefault(); // Prevent scrolling while touching
            handleMove(event.touches[0]);
        };
        const handleTouchEnd = () => setIsDragging(false);

        // Mouse Events
        canvas.addEventListener("mousedown", handleMouseDown);
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        // Touch Events
        canvas.addEventListener("touchstart", handleTouchStart, { passive: false });
        window.addEventListener("touchmove", handleTouchMove, { passive: false });
        window.addEventListener("touchend", handleTouchEnd);

        return () => {
            canvas.removeEventListener("mousedown", handleMouseDown);
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);

            canvas.removeEventListener("touchstart", handleTouchStart);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", handleTouchEnd);
        };
    }, [isDragging]);

    const handleStart = (event) => {
        const { x, y } = getRelativeCoords(event);

        if (x >= KNOBS.gain.x && x <= KNOBS.gain.x + KNOBS.size && y >= KNOBS.gain.y && y <= KNOBS.gain.y + KNOBS.size) {
            startDragging("gain", y, gainKnobIndex);
        } else if (x >= KNOBS.tone.x && x <= KNOBS.tone.x + KNOBS.size && y >= KNOBS.tone.y && y <= KNOBS.tone.y + KNOBS.size) {
            startDragging("tone", y, toneKnobIndex);
        } else if (x >= KNOBS.level.x && x <= KNOBS.level.x + KNOBS.size && y >= KNOBS.level.y && y <= KNOBS.level.y + KNOBS.size) {
            startDragging("level", y, levelKnobIndex);
        }
    };

    const handleMove = (event) => {
        if (!isDragging || !activeKnob.current) return;

        const { y } = getRelativeCoords(event);
        let deltaY = initialClickY.current - y;

        let newIndex = Math.round(initialKnobIndex.current + (deltaY / KNOB_PIXEL_RANGE) * 299);
        newIndex = Math.max(0, Math.min(299, newIndex));

        if (activeKnob.current === "gain") setGainKnobIndex(newIndex);
        if (activeKnob.current === "tone") setToneKnobIndex(newIndex);
        if (activeKnob.current === "level") setLevelKnobIndex(newIndex);
    };

    const startDragging = (knobType, y, knobIndex) => {
        setIsDragging(true);
        activeKnob.current = knobType;
        initialClickY.current = y;
        initialKnobIndex.current = knobIndex;
    };

    const getRelativeCoords = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
        };
    };

    return (
        <div style={{ textAlign: "center" }}>
            <canvas ref={canvasRef} style={{ backgroundColor: "rgb(25,26,27)", cursor: isDragging ? "grabbing" : "pointer" }} />
            <p>Gain: {gainKnobIndex}/299 | Tone: {toneKnobIndex}/299 | Level: {levelKnobIndex}/299</p>
        </div>
    );
};

export default GainCanvas;


// import React, { useRef, useEffect, useState } from "react";

// const GainCanvas = () => {
//     const canvasRef = useRef(null);
//     const imageRef = useRef(null);
//     const gainKnobRef = useRef([]);
//     const toneKnobRef = useRef([]);
//     const levelKnobRef = useRef([]);
    
//     const [gainKnobIndex, setGainKnobIndex] = useState(0);
//     const [toneKnobIndex, setToneKnobIndex] = useState(0);
//     const [levelKnobIndex, setLevelKnobIndex] = useState(0);
    
//     const [isDragging, setIsDragging] = useState(false);
//     const activeKnob = useRef(null); // Track which knob is being dragged
//     const initialClickY = useRef(null);
//     const initialKnobIndex = useRef(null);

//     const width = 300;
//     const height = 500;
//     const KNOB_PIXEL_RANGE = 150; // Drag sensitivity range

//     const KNOBS = {
//         gain: { x: width * 0.14, y: height * 0.09 },
//         tone: { x: width * 0.39, y: height * 0.14 },
//         level: { x: width * 0.65, y: height * 0.09 },
//         size: width * 0.2, // Size of each knob
//     };

//     useEffect(() => {
//         const canvas = canvasRef.current;
//         const ctx = canvas.getContext("2d");

//         canvas.width = width;
//         canvas.height = height;

//         const background = new Image();
//         background.src = "/MK1OverdriveImages/MK1OverdriveOnRed.png";

//         // Load knob images
//         for (let i = 0; i < 300; i++) {
//             gainKnobRef.current[i] = new Image();
//             gainKnobRef.current[i].src = `/MK1OverdriveImages/MK1Overdrive_${i}.png`;

//             toneKnobRef.current[i] = new Image();
//             toneKnobRef.current[i].src = `/MK1OverdriveImages/MK1Overdrive_${i}.png`;

//             levelKnobRef.current[i] = new Image();
//             levelKnobRef.current[i].src = `/MK1OverdriveImages/MK1Overdrive_${i}.png`;
//         }

//         const draw = () => {
//             ctx.clearRect(0, 0, canvas.width, canvas.height);

//             // Draw background
//             if (imageRef.current) {
//                 ctx.drawImage(imageRef.current, width * 0.05, height * 0.05, width * 0.9, height * 0.9);
//             }

//             // Draw knobs
//             ctx.drawImage(gainKnobRef.current[gainKnobIndex], KNOBS.gain.x, KNOBS.gain.y, KNOBS.size, KNOBS.size);
//             ctx.drawImage(toneKnobRef.current[toneKnobIndex], KNOBS.tone.x, KNOBS.tone.y, KNOBS.size, KNOBS.size);
//             ctx.drawImage(levelKnobRef.current[levelKnobIndex], KNOBS.level.x, KNOBS.level.y, KNOBS.size, KNOBS.size);
//         };

//         draw();

//         background.onload = () => {
//             imageRef.current = background;
//             draw();
//         };
//     }, [gainKnobIndex, toneKnobIndex, levelKnobIndex]);

//     useEffect(() => {
//         const canvas = canvasRef.current;

//         const handleMouseDown = (event) => {
//             const { x, y } = getRelativeCoords(event);

//             // Detect if user clicks inside a knob
//             if (x >= KNOBS.gain.x && x <= KNOBS.gain.x + KNOBS.size && y >= KNOBS.gain.y && y <= KNOBS.gain.y + KNOBS.size) {
//                 startDragging("gain", y, gainKnobIndex);
//             } else if (x >= KNOBS.tone.x && x <= KNOBS.tone.x + KNOBS.size && y >= KNOBS.tone.y && y <= KNOBS.tone.y + KNOBS.size) {
//                 startDragging("tone", y, toneKnobIndex);
//             } else if (x >= KNOBS.level.x && x <= KNOBS.level.x + KNOBS.size && y >= KNOBS.level.y && y <= KNOBS.level.y + KNOBS.size) {
//                 startDragging("level", y, levelKnobIndex);
//             }
//         };

//         const handleMouseMove = (event) => {
//             if (!isDragging || !activeKnob.current) return;

//             const { y } = getRelativeCoords(event);
//             let deltaY = initialClickY.current - y;

//             // Normalize drag movement to knob index (0-299)
//             let newIndex = Math.round(initialKnobIndex.current + (deltaY / KNOB_PIXEL_RANGE) * 299);
//             newIndex = Math.max(0, Math.min(299, newIndex));

//             if (activeKnob.current === "gain") setGainKnobIndex(newIndex);
//             if (activeKnob.current === "tone") setToneKnobIndex(newIndex);
//             if (activeKnob.current === "level") setLevelKnobIndex(newIndex);
//         };

//         const handleMouseUp = () => {
//             setIsDragging(false);
//             activeKnob.current = null;
//         };

//         canvas.addEventListener("mousedown", handleMouseDown);
//         window.addEventListener("mousemove", handleMouseMove);
//         window.addEventListener("mouseup", handleMouseUp);

//         return () => {
//             canvas.removeEventListener("mousedown", handleMouseDown);
//             window.removeEventListener("mousemove", handleMouseMove);
//             window.removeEventListener("mouseup", handleMouseUp);
//         };
//     }, [isDragging]);

//     // Function to store knob data when user clicks
//     const startDragging = (knobType, y, knobIndex) => {
//         setIsDragging(true);
//         activeKnob.current = knobType;
//         initialClickY.current = y;
//         initialKnobIndex.current = knobIndex;
//     };

//     // Get mouse position relative to canvas
//     const getRelativeCoords = (event) => {
//         const canvas = canvasRef.current;
//         const rect = canvas.getBoundingClientRect();
//         return {
//             x: event.clientX - rect.left,
//             y: event.clientY - rect.top,
//         };
//     };

//     return (
//         <div style={{ textAlign: "center" }}>
//             <canvas ref={canvasRef} style={{ backgroundColor: "rgb(25,26,27)", cursor: isDragging ? "grabbing" : "pointer" }} />
//             <p>Gain: {gainKnobIndex}/299 | Tone: {toneKnobIndex}/299 | Level: {levelKnobIndex}/299</p>
//         </div>
//     );
// };

// export default GainCanvas;

